import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { EnvironmentConfig, ImageUtils, ModalService } from "../../../core/core";
import { InventoryService } from "../../services/inventory.service";
import { ToastrService } from "ngx-toastr";
import Cropper from "cropperjs/dist/cropper.esm.js";
import { EditInventoryImagesComponent } from "../edit-inventory-images/edit-inventory-images.component";

declare var environment: EnvironmentConfig;

@Component({
  selector: "common-inventory-images-private",
  templateUrl: "./inventory-images-private.component.html",
  styleUrls: ["../inventory-images/inventory-images.component.scss"],
})
export class InventoryImagesPrivateComponent implements OnInit {
  @Input() inventoryItem: any;
  @Input() showAdminActions: boolean = true;
  @Input() hideDropdownActions: boolean = false;
  @Input() hideInlineVerificationHint = false;

  @Output() imageUploaded = new EventEmitter<number>();
  @Output() imageDeleted = new EventEmitter<any>();

  @ViewChild("modalContent") modalContent: HTMLElement;
  @ViewChild("croppr") croppr: HTMLElement;

  imgCropperSrc: string;
  cropInstance: any;

  images = [{ name: "Serial Number Plate" }, { name: "Hour Meter" }];
  imageFiles: any[] = [];
  imagePaths: string[] = [];
  imageIndexWeAreUploading: number;
  imagesCollectionForEditing: any[];

  constructor(
    private inventoryService: InventoryService,
    private toastrService: ToastrService,
    private domSanitizer: DomSanitizer,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    this.inventoryService.getImages(this.inventoryItem.id, this.inventoryItem.accountId, false, true).subscribe(result => {
      this.imagePaths = result.map(i => {
        return i?.storageModel?.storagePath ? `${environment.baseImagePath}/${i.storageModel.storagePath}` : "";
      });
      this.imagesCollectionForEditing = result;
    });
  }

  setIndexForImage(index) {
    this.imageIndexWeAreUploading = index;
  }

  onSingleImageChange(event) {
    const files = event.target.files;
    this.imageFiles[this.imageIndexWeAreUploading] = files[0];
    // even tho we are using cropper now we still want to resize image
    ImageUtils.resizeImage({
      file: files[0],
      maxSize: 1600,
    })
      .then((resizedImage: Blob) => {
        const objectUrl = window.URL.createObjectURL(resizedImage);
        this.imgCropperSrc = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
        this.modalService.largeModal(this.modalContent, true);

        return;
      })
      .catch(function (err) {
        console.error(err);
      });
  }

  imgOnLoad(event) {
    if (this.imageIndexWeAreUploading == null) {
      return;
    }

    const imageFile = this.imageFiles[this.imageIndexWeAreUploading];

    const myRenamedFile = new File([imageFile], this.images[this.imageIndexWeAreUploading].name);
    this.inventoryService
      .uploadImage(this.inventoryItem.id, this.inventoryItem.accountId, this.imageIndexWeAreUploading, myRenamedFile, true)
      .subscribe(result => {
        this.toastrService.success("Successfully uploaded photo");
        this.imageUploaded.emit(this.imageFiles.length);
      });
  }

  imageLoadedForCropper(event) {
    this.cropInstance = new Cropper(event.target, {
      viewMode: 1,
      dragMode: "move",
      aspectRatio: 16 / 9,
      restore: false,
      guides: true,
      center: false,
      highlight: true,
      background: false,
      cropBoxMovable: false,
      cropBoxResizable: false,
      toggleDragModeOnDblclick: false,
    });

    setTimeout(() => {
      const containerData = this.cropInstance.getContainerData();
      this.cropInstance.zoomTo(0, {
        x: containerData.width / 2,
        y: containerData.height / 2,
      });
    }, 800);
  }

  zoomOut() {
    this.cropInstance?.zoom(-0.1);
  }

  zoomIn() {
    this.cropInstance?.zoom(0.1);
  }

  saveCroppedPhoto(modal) {
    this.cropInstance.getCroppedCanvas().toBlob(blob => {
      const objectUrl = window.URL.createObjectURL(blob);
      this.imagePaths[this.imageIndexWeAreUploading] = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
      var file = new File([blob], this.imageFiles[this.imageIndexWeAreUploading].name);
      this.imageFiles[this.imageIndexWeAreUploading] = file;
      modal.close();
    });
  }

  editImage(image, index: number) {
    this.imageIndexWeAreUploading = index;
    const modalRef = this.modalService.largeModal(EditInventoryImagesComponent, true);
    modalRef.componentInstance.originalImageSource = this.imagePaths[index];

    modalRef.result.then(
      blob => {
        const objectUrl = window.URL.createObjectURL(blob);
        this.imagePaths[this.imageIndexWeAreUploading] = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
        const storedName = this.imagesCollectionForEditing?.[this.imageIndexWeAreUploading]?.name;
        const file = new File([blob], storedName || this.images[this.imageIndexWeAreUploading].name);
        this.imageFiles[this.imageIndexWeAreUploading] = file;
      },
      () => {
        this.imageIndexWeAreUploading = null;
      },
    );
  }

  deleteImage(index) {
    this.inventoryService.deleteImage(this.inventoryItem.id, this.inventoryItem.accountId, index, true).subscribe(result => {
      this.imagePaths[index] = "";
      this.toastrService.success("Successfully deleted photo");
      this.imageDeleted.emit();
    });
  }
}
